import React, { memo } from 'react';
import { Box, ButtonBase, Typography, alpha, useTheme } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import type { IdCardLayout } from '../../../types/idCards';
import { LAYOUTS } from '../catalog';
import IdCardPreview from '../IdCardPreview';
import type { CardFaceProps } from '../render/faces/shared';

interface Props {
  value: IdCardLayout;
  onChange: (layout: IdCardLayout) => void;
  /** The card as it is now, so each option shows this design in that layout. */
  face: CardFaceProps;
}

/** The four layouts, each drawn with the design's own colours and details. */
const LayoutPicker: React.FC<Props> = ({ value, onChange, face }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
      {LAYOUTS.map(l => {
        const selected = l.key === value;
        const portrait = l.orientation === 'portrait';
        return (
          <ButtonBase
            key={l.key}
            onClick={() => onChange(l.key)}
            aria-pressed={selected}
            sx={{
              position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'left',
              borderRadius: 2, border: 1.5, overflow: 'hidden',
              borderColor: selected ? 'primary.main' : 'divider',
              boxShadow: selected ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.14)}` : 'none',
              transition: 'border-color .15s, box-shadow .15s',
              '&:hover': { borderColor: selected ? 'primary.main' : alpha(theme.palette.primary.main, 0.45) },
            }}
          >
            <Box sx={{ height: 92, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.text.primary, 0.035) }}>
              <IdCardPreview {...face} design={{ ...face.design, layout: l.key }} side="front" width={portrait ? 50 : 118} flat />
            </Box>
            <Box sx={{ px: 1.25, py: 1 }}>
              <Typography variant="body2" fontWeight={600}>{l.label}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>{l.description}</Typography>
            </Box>
            {selected && <CheckCircle color="primary" sx={{ position: 'absolute', top: 6, right: 6, fontSize: 18, bgcolor: 'background.paper', borderRadius: '50%' }} />}
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default memo(LayoutPicker);
